import React, { useEffect, useRef, useState } from "react";
import PageTitle from "../../../common/PageTitle";
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Autocomplete,
} from "@mui/material";
import { tableData, tableHeader } from "../../../utils";
import {
  deleteAPI,
  getAPI,
  getExportAPI,
  getExportPdfAPI,
  postAPI,
  putAPI,
} from "../../../network";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmptyTable from "../../../common/EmptyTable";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { BASE_URL } from "../../../../utils/axiosSetup";
import issueFaultData from "./Fsrs.json";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from 'react-helmet';

const FSR = () => {
  let companyId = localStorage.getItem("companyIdLS");
  if (companyId === null) companyId = undefined;

  const location = useLocation();
  const receivedData = location.state;

  // --- Raw data vs. filtered list ---
  const [allFsrs, setAllFsrs] = useState([]);      // full dataset
  const [fsrList, setFsrList] = useState([]);      // filtered view
  const navigate = useNavigate()
  // --- Loading & pagination ---
  const [loader, setLoader] = useState(false);
  const [page, setPage] = useState(0);
  const [perPages] = useState([10, 25, 50, 100]);
  const [perPage, setPerPage] = useState(10);

  // --- Filter state (now includes date and fsrId) ---
  const [filter, setFilter] = useState({
    bType: "",
    bName: "",
    bFloor: "",
    bSpace: "",
    bSpaceId: "",
    bIssueType: "",
    bFaultType: "",
    bStatus: "",
    startDate: "",
    endDate: "",
    fsrId: "",
  });

  // --- Reference data lists ---
  const [buildingTypeList, setBuildingTypeList] = useState([]);
  const [buildingNameList, setBuildingNameList] = useState([]);
  const [buildingFloorList, setBuildingFloorList] = useState([]);
  const [buildingSpaceList, setBuildingSpaceList] = useState([]);
  const [buildingSpaceIdList, setBuildingSpaceIdList] = useState([]);
  const [buildingAssetIdList, setBuildingAssetIdList] = useState([]);
  const [vendor, setVendor] = useState([]);
  const [assignee, setAssignee] = useState([]);
  const [issueTypes, setIssueTypes] = useState([]);
  const [faultTypes, setFaultTypes] = useState([]);

  // --- Add / Edit form state ---
  const [modalOpen, setModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [inputs, setInputs] = useState({
    companyId,
    areaId: "",
    buildingTypes: "",
    buildingName: "",
    buildingFloor: "",
    buildingSpaceType: "",
    issueType: "",
    faultType: "",
    priorityLevel: "",
    issueDescription: "",
    loggedBy: "",
    file: null,
  });
  const [buildingInputs, setBuildingInputs] = useState({
    buildingSpaceId: "",
    buildingTypes: "",
    buildingName: "",
    buildingFloor: "",
    buildingSpaceType: "",
    issueType: "",
    faultType: "",
    priorityLevel: "",
    issueDescription: "",
    assignee: "",
    buildingVendor: "",
    assetId: "",
    status: "",
    woNumber: "",
    actionTaken: "",
    cost: 0,
    jobAttachment: "",
    _id: "",
    requestId: "",
  });

  // --- File upload refs/state for Edit form ---
  const fileInputRef = useRef(null);
  const [fileName, setFileName] = useState("Select 16- Job Attachment File");
  const [file, setFile] = useState(null);

  // --- Miscellaneous state ---
  const [dropdownOpened, setDropdownOpened] = useState(false);
  const [fSRFile, setFSRFile] = useState("");
  const [selectedIssueType, setSelectedIssueType] = useState("");
  const [selectedFaultType, setSelectedFaultType] = useState("");
  const [urlF, setUrlF] = useState("");
  const [permissionList, setPermissionList] = useState([]);
  // (you still have your canAdd/canEdit logic based on permissionList)

  // --------------------------------------------
  // Pagination handlers
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setPerPage(+e.target.value);
    setPage(0);
  };
  // --- HANDLERS FOR ADD / EDIT FORMS ---
  const onChange = async (e) => {
    e.preventDefault();
    setInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    if (e.target.name === "issueType") {
      setSelectedIssueType(e.target.value);
      fetchFaultTypes(e.target.value);
    }
    if (e.target.name === "faultType") {
      setSelectedFaultType(e.target.value);
      const fileObj = faultTypes.find((f) => f._id === e.target.value);
      setFSRFile(fileObj?.fileUrl || "");
    }
  };

  const editChange = async (e) => {
    e.preventDefault();
    setBuildingInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    if (e.target.name === "issueType") {
      setSelectedIssueType(e.target.value);
      fetchFaultTypes(e.target.value);
    }
    if (e.target.name === "faultType") {
      setSelectedFaultType(e.target.value);
      const fileObj = faultTypes.find((f) => f._id === e.target.value);
      setFSRFile(fileObj?.fileUrl || "");
    }
  };

  // Add‐form file
  const handleFileAddChange = (e) => {
    setInputs((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  // Edit‐form file
  const handleClick = () => fileInputRef.current.click();
  const handleFileChange = (e) => {
    const sel = e.target.files[0];
    if (sel) {
      setFileName(sel.name);
      setFile(sel);
      setBuildingInputs((p) => ({ ...p, jobAttachment: sel }));
    } else {
      setFileName("Select 16- Job Attachment File");
    }
  };

  // Single submit for both add & edit
  const onSubmit = async (e) => {
    e.preventDefault();
    editMode ? editBuildingTypee() : postBuildingType();
  };

  // --- CLIENT‐SIDE FILTER + FETCH LOGIC ---
  const fetchFsrs = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/facilitiesRequest?companyId=${companyId}`);
    setLoader(false);
    if (resp) {
      setAllFsrs(resp);
      setFsrList(resp);
    }
  };

  // Re-apply filters any time we load or change filter state
  useEffect(() => {
    let out = allFsrs;
    if (filter.bType) out = out.filter(i => i.buildingTypes?._id === filter.bType);
    if (filter.bName) out = out.filter(i => i.buildingName?._id === filter.bName);
    if (filter.bFloor) out = out.filter(i => i.buildingFloor?._id === filter.bFloor);
    if (filter.bSpace) out = out.filter(i => i.buildingSpaceType?._id === filter.bSpace);
    if (filter.bSpaceId) out = out.filter(i => i.buildingSpaceId?._id === filter.bSpaceId);
    if (filter.bIssueType) out = out.filter(i => i.issueType?._id === filter.bIssueType);
    if (filter.bFaultType) out = out.filter(i => i.faultType?._id === filter.bFaultType);
    if (filter.bStatus) out = out.filter(i => i.status === filter.bStatus);

    if (filter.startDate)
      out = out.filter(i => i.createdAt.split("T")[0] >= filter.startDate);
    if (filter.endDate)
      out = out.filter(i => i.createdAt.split("T")[0] <= filter.endDate);

    if (filter.fsrId)
      out = out.filter(i => i.requestId === parseInt(filter.fsrId));

    setFsrList(out);
    setPage(0);
  }, [allFsrs, filter]);

  // Auto-select fsrId if present in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fsrParam = params.get("fsrId");
    if (fsrParam) {
      setFilter(f => ({ ...f, fsrId: fsrParam }));
    }

  }, [location.search]);

  // --- REFERENCE DATA FETCHERS ---
  const fetchIssueTypes = async () => {
    if (!companyId) return;
    const resp = await getAPI(`/building/get-issuetype?companyId=${companyId}`);
    setIssueTypes(resp || []);
  };
  const fetchFaultTypes = async issueType => {
    if (!companyId) return;
    const resp = await getAPI(`/building/get-fault?companyId=${companyId}&issueType=${issueType}`);
    setFaultTypes(resp || []);
  };
  const getVendor = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/vendor?companyId=${companyId}`);
    setVendor(resp || []);
    setLoader(false);
  };
  const getAssignee = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/Assignee?companyId=${companyId}`);
    setAssignee(resp || []);
    setLoader(false);
  };
  const getBuildingType = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/type?companyId=${companyId}`);
    setBuildingTypeList(resp || []);
    setLoader(false);
  };
  const getBuildingName = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/name?companyId=${companyId}`);
    setBuildingNameList(resp || []);
    setLoader(false);
  };
  const getBuildingFloor = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/floor?companyId=${companyId}`);
    setBuildingFloorList(resp || []);
    setLoader(false);
  };
  const getBuildingSpace = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/SpaceType?companyId=${companyId}`);
    setBuildingSpaceList(resp || []);
    setLoader(false);
  };
  const getBuildingSpaceId = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/SpaceId?companyId=${companyId}`);
    setBuildingSpaceIdList(resp || []);
    setLoader(false);
  };
  const getAssetId = async () => {
    setLoader(true);
    const resp = await getAPI(`/building/AssetId?companyId=${companyId}`);
    setBuildingAssetIdList(resp || []);
    setLoader(false);
  };

  // --- CRUD OPERATIONS ---
  const postBuildingType = async () => {
    setLoader(true);
    // (perform your existing validations here)
    const fd = new FormData();
    Object.entries(inputs).forEach(([k, v]) => {
      if (v != null && v !== "")
        fd.append(k === "file" ? "picture" : k, v);
    });
    const resp = await postAPI(`/building/facilitiesRequest`, fd);
    setLoader(false);
    if (resp) {
      fetchFsrs();
      setModalOpen(false);
      setInputs(i => ({
        ...i,
        areaId: "",
        buildingTypes: "",
        buildingName: "",
        buildingFloor: "",
        buildingSpaceType: "",
        issueType: "",
        faultType: "",
        priorityLevel: "",
        issueDescription: "",
        file: null,
      }));
      setFSRFile("");
    }
  };

  const editFSrType = (e, _id) => {
    e.preventDefault();
    setEditModal(true);
    setEditMode(true);
    const data = allFsrs.find(i => i._id === _id);
    if (!data) return;
    setBuildingInputs({
      ...data,
      buildingTypes: data.buildingTypes?._id,
      buildingName: data.buildingName?._id,
      buildingFloor: data.buildingFloor?._id,
      buildingSpaceType: data.buildingSpaceType?._id,
      buildingSpaceId: data.buildingSpaceId?._id,
      issueType: data.issueType?._id,
      faultType: data.faultType?._id,
      assignee: data.assignee?._id,
      buildingVendor: data.buildingVendor?._id,
      buildingAssetId: data.buildingAssetId?._id,
      cost: data.cost,
      _id: data._id,
      requestId: data.requestId,
    });
    fetchFaultTypes(data.issueType?._id);
    const fileObj = faultTypes.find(f => f._id === data.faultType?._id);
    setFSRFile(fileObj?.fileUrl || "");
  };

  const editBuildingTypee = async () => {
    setLoader(true);
    const fd = new FormData();
    Object.entries(buildingInputs).forEach(([k, v]) => {
      if (v != null && v !== "")
        fd.append(k === "jobAttachment" ? "picture" : k, v);
    });
    const resp = await putAPI(
      `/building/facilitiesRequest/${buildingInputs._id}`, fd
    );
    setLoader(false);
    if (resp) {
      fetchFsrs();
      setEditModal(false);
      setFSRFile("");
    }
  };

  const deleteBuildingType = async _id => {
    setLoader(true);
    const resp = await deleteAPI(`/building/facilitiesRequest/${_id}`);
    setLoader(false);
    if (resp) fetchFsrs();
  };

  // --- EXPORTS ---
  const getExportToExcel = async () => {
    setLoader(true);
    await getExportAPI(
      `${BASE_URL}/building/facilitiesRequest/export`,
      "Facility services and requests"
    );
    setLoader(false);
  };
  const getExportPdf = async () => {
    setLoader(true);
    await getExportPdfAPI(
      `${BASE_URL}/building/facilitiesRequest/pdfexport/${buildingInputs._id}`,
      `FSR-${buildingInputs.status}-${buildingInputs.requestId}`
    );
    setLoader(false);
  };

  // --- STYLE FOR EDIT MODAL ---
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: "background.paper",
    border: "2px solid #fff",
    borderRadius: "5px",
    boxShadow: 24,
    p: 4,
  };

  // --- INITIAL DATA LOAD ---
  useEffect(() => {
    fetchFsrs();
    getBuildingSpaceId();
    getBuildingSpace();
    getBuildingFloor();
    getBuildingName();
    getBuildingType();
    fetchIssueTypes();
    getVendor();
    getAssignee();
    getAssetId();
  }, []);

  // --- SET LOGGED BY ---
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    if (user.email) {
      setInputs(i => ({ ...i, loggedBy: user.email }));
    }
  }, []);

  // --- PERMISSIONS + URL-STATE ---
  useEffect(() => {
    const perms = JSON.parse(localStorage.getItem("permisionlist")) || [];
    setPermissionList(perms);
    if (receivedData) {
      setFilter(f => ({ ...f, bIssueType: receivedData }));
      fetchFaultTypes(receivedData);
    }
  }, [receivedData]);

  const actionPermissions = permissionList[0]?.actionPermissions || [];
  const submenuPermissions = permissionList[0]?.submenuPermissions || [];
  const canAdd = actionPermissions.some(p => p.action === "Add" && submenuPermissions.some(s => s._id === p.submenu));
  const canDelete = actionPermissions.some(p => p.action === "Delete" && submenuPermissions.some(s => s._id === p.submenu));
  const canEdit = actionPermissions.some(p => p.action === "Edit" && submenuPermissions.some(s => s._id === p.submenu));
  const showBothButtons = actionPermissions.length === 0;

  // --- END OF BLOCK 2; NEXT: the JSX UI in the `return`  ---
  return (
    <div style={{ width: "100%" }}>
      <Helmet>
        <title>Digital Front Door</title>
      </Helmet>
      <PageTitle title={"Digital Front Door"} />
      <p style={{ marginLeft: 12, width: "70%", paddingBottom: 20 }}>
        <b style={{ fontWeight: 600 }}>Note: </b>
        Use the filters below to check for an open issue before logging a new one. If one exists, or if a Knowledge Article provides steps to resolve it, no new request is needed.
      </p>

      <div style={{ width: "78%", paddingBottom: 20, marginTop: 60 }}>
        {(showBothButtons || canAdd) && (
          <Button
            onClick={() => setModalOpen(true)}
            variant="contained"
            sx={{ backgroundColor: "#75859D", float: "left", width: 170, height: 52, mx: 2 }}
          >
            <AddCircleIcon /> &nbsp; Add FSR
          </Button>
        )}
        <Button
          variant="contained"
          sx={{ backgroundColor: "#75859D", float: "left", width: 170, height: 52, mx: 2 }}
          onClick={getExportToExcel}
        >
          Export
        </Button>
      </div>

      <Grid container spacing={2} sx={{ width: "70%", marginTop: "60px" }}>
        {/* Building Type */}
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Building Type</InputLabel>
            <Select
              value={filter.bType}
              label="Building Type"
              onChange={(e) => setFilter(f => ({ ...f, bType: e.target.value }))}
            >
              {buildingTypeList.map(b => (
                <MenuItem key={b._id} value={b._id}>{b.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Building Name */}
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Building Name</InputLabel>
            <Select
              value={filter.bName}
              label="Building Name"
              onChange={(e) => setFilter(f => ({ ...f, bName: e.target.value }))}
            >
              {buildingNameList
                .filter(n => n.buildingTypes?._id === filter.bType)
                .map(n => (
                  <MenuItem key={n._id} value={n._id}>{n.title}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Building Floor */}
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Building Floor</InputLabel>
            <Select
              value={filter.bFloor}
              label="Building Floor"
              onChange={(e) => setFilter(f => ({ ...f, bFloor: e.target.value }))}
            >
              {buildingFloorList
                .filter(floor => floor.buildingName?._id === filter.bName)
                .map(floor => (
                  <MenuItem key={floor._id} value={floor._id}>{floor.title}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Space Type */}
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Space Type</InputLabel>
            <Select
              value={filter.bSpace}
              label="Space Type"
              onChange={(e) => setFilter(f => ({ ...f, bSpace: e.target.value }))}
            >
              {buildingSpaceList
                .filter(s => s.buildingFloor?._id === filter.bFloor)
                .map(s => (
                  <MenuItem key={s._id} value={s._id}>{s.title}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Space ID */}
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Space ID</InputLabel>
            <Select
              value={filter.bSpaceId}
              label="Space ID"
              onChange={(e) => setFilter(f => ({ ...f, bSpaceId: e.target.value }))}
            >
              {buildingSpaceIdList
                .filter(si => si.buildingSpace?._id === filter.bSpace)
                .map(si => (
                  <MenuItem key={si._id} value={si._id}>{si.areaId}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Issue Type */}
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Issue Type</InputLabel>
            <Select
              value={filter.bIssueType}
              label="Issue Type"
              onOpen={fetchIssueTypes}
              onChange={e => {
                setFilter(f => ({ ...f, bIssueType: e.target.value }));
                fetchFaultTypes(e.target.value);
              }}
            >
              {issueTypes.map(it => (
                <MenuItem key={it._id} value={it._id}>{it.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Fault Type */}
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Fault Type</InputLabel>
            <Select
              value={filter.bFaultType}
              label="Fault Type"
              onChange={e => setFilter(f => ({ ...f, bFaultType: e.target.value }))}
            >
              {faultTypes.map(ft => (
                <MenuItem key={ft._1d} value={ft._id}>{ft.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Status */}
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={filter.bStatus}
              label="Status"
              onChange={e => setFilter(f => ({ ...f, bStatus: e.target.value }))}
            >
              {["Submitted/Open", "Inactive", "Assigned", "Deferred", "On Hold", "Completed", "Duplicate"]
                .map(s => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Start Date */}
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="Start Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filter.startDate}
            onChange={e => setFilter(f => ({ ...f, startDate: e.target.value }))}
          />
        </Grid>

        {/* End Date */}
        <Grid item xs={3}>
          <TextField
            fullWidth
            label="End Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filter.endDate}
            onChange={e => setFilter(f => ({ ...f, endDate: e.target.value }))}
          />
        </Grid>

        {/* FSR ID Autocomplete */}
        <Grid item xs={3}>
          <Autocomplete
            fullWidth
            options={[...new Set(allFsrs.map(i => i.requestId))]}
            value={filter.fsrId || null}
            onChange={(_, v) => setFilter(f => ({ ...f, fsrId: v || "" }))}
            renderInput={params => <TextField {...params} label="FSR ID" />}
          />
        </Grid>
      </Grid>
      <div style={{ marginTop: "50px" }}>
        <TableContainer
          component={Paper}
          sx={{
            mx: "0.8rem",
            width: "100%",
            my: "6rem",
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <Table aria-label="custom pagination table" className="responsive-table">
            <TableHead>
              <TableRow className="table-header">
                {[
                  "Request ID",
                  "Date Logged",
                  "Building Type",
                  "Building Name",
                  "Building Floor",
                  "Space Type",
                  "Space Id",
                  "Priority Level",
                  "Service Type",
                  "Fault Type",
                  "Issue Description",
                  "Image",
                  "Logged By",
                  "Assignee",
                  "Vendor",
                  "WO Number",
                  "Status",
                  "Action Taken",
                  "Cost",
                  "Asset Id",
                  "Date Closed",
                  "Action",
                ].map((h) => (
                  <TableCell key={h} sx={tableHeader}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {fsrList
                .slice(page * perPage, page * perPage + perPage)
                .map((item) => (
                  <TableRow key={item._id}>
                    <TableCell sx={tableData}>{item.requestId}</TableCell>
                    <TableCell sx={tableData}>{item.createdAt.split("T")[0]}</TableCell>
                    <TableCell sx={tableData}>{item.buildingTypes?.title}</TableCell>
                    <TableCell sx={tableData}>{item.buildingName?.title}</TableCell>
                    <TableCell sx={tableData}>{item.buildingFloor?.title}</TableCell>
                    <TableCell sx={tableData}>{item.buildingSpaceType?.title}</TableCell>
                    <TableCell sx={tableData}>{item.buildingSpaceId?.areaId}</TableCell>
                    <TableCell sx={tableData}>{item.priorityLevel}</TableCell>
                    <TableCell sx={tableData}>{item.issueType?.title}</TableCell>
                    <TableCell sx={tableData}>{item.faultType?.title}</TableCell>
                    <TableCell sx={tableData}>{item.issueDescription}</TableCell>
                    <TableCell>
                      {item.image ? (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.open(item.image, "_blank")}
                        >
                          Open
                        </Button>
                      ) : (
                        "No File"
                      )}
                    </TableCell>
                    <TableCell sx={tableData}>{item.loggedBy}</TableCell>
                    <TableCell sx={tableData}>{item.assignee?.name}</TableCell>
                    <TableCell sx={tableData}>{item.buildingVendor?.vendorName}</TableCell>
                    <TableCell sx={tableData}>{item.woNumber}</TableCell>
                    <TableCell sx={tableData}>{item.status}</TableCell>
                    <TableCell sx={tableData}>{item.actionTaken}</TableCell>
                    <TableCell sx={tableData}>{item.cost}</TableCell>
                    <TableCell sx={tableData}>{item.buildingAssetId?.assetId}</TableCell>
                    <TableCell sx={tableData}>
                      {item.status === "Completed"
                        ? item.updatedAt.split("T")[0]
                        : ""}
                    </TableCell>
                    <TableCell sx={tableData}>
                      {(showBothButtons || canEdit) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          onClick={(e) => editFSrType(e, item._id)}
                        >
                          <EditIcon />
                        </Button>
                      )}
                      {(showBothButtons || canDelete) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => deleteBuildingType(item._id)}
                        >
                          <DeleteIcon />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              {fsrList.length === 0 && <EmptyTable colSpan={22} />}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={perPages}
                  count={fsrList.length}
                  rowsPerPage={perPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  SelectProps={{
                    inputProps: { "aria-label": "rows per page" },
                    native: true,
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
      {/* Add-FSR Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: window.innerWidth < 600 ? "95%" : "50%",
            maxHeight: "90vh",
            backgroundColor: "#fff",
            boxShadow: "0px 0px 24px rgba(0, 0, 0, 0.2)",
            padding: "16px",
            overflowY: "auto",
            borderRadius: "8px",
          }}
        >
          <PageTitle title={"Add FSR"} />
          <Grid
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: "10px",
              marginTop: "16px",
            }}
          >
            <FormControl fullWidth style={{ margin: "0 10px" }}>
              <InputLabel id="demo-simple-select-label">Issue Type 1*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="issueType"
                value={inputs?.issueType}
                label="Priority Level 1*"
                onChange={onChange}
              >
                {issueTypes.length > 0 ? (
                  issueTypes.map((issueType) => (
                    <MenuItem key={issueType._id} value={issueType._id}>
                      {issueType.title}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No issue types available</MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ margin: "0 10px" }}>
              <InputLabel id="demo-simple-select-label">Fault Type 2*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="faultType"
                value={inputs?.faultType}
                label="Fault Type 2*"
                onChange={onChange}
              >
                {faultTypes.length > 0 ? (
                  faultTypes.map((faultType) => (
                    <MenuItem key={faultType._id} value={faultType._id}>
                      {faultType.title}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No fault available</MenuItem>
                )}
              </Select>
            </FormControl>
            {fSRFile && (
              <Button
                variant="outlined"
                size="small"
                style={{ marginLeft: "10px", width: "100%" }}
                onClick={() => window.open(fSRFile, "_blank")}
              >
                Refer to this Resource before submitting a service request
              </Button>
            )}
            {fSRFile && <br />}
            <FormControl fullWidth style={{ margin: "0 10px" }}>
              <InputLabel id="demo-simple-select-label">Select Building Type 3*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingTypes"
                value={inputs?.buildingTypes}
                label="Select Building Type 3"
                onChange={onChange}
              >
                {buildingTypeList?.map((item, index) => (
                  <MenuItem key={index} value={item?._id}>
                    {item?.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ margin: "0 10px" }}>
              <InputLabel id="demo-simple-select-label">Select Building Name 4*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingName"
                value={inputs?.buildingName}
                label="Select Building Name 4*"
                onChange={onChange}
              >
                {buildingNameList
                  ?.filter((name) => name?.buildingTypes?._id === inputs?.buildingTypes)
                  ?.map((item, index) => (
                    <MenuItem key={index} value={item?._id}>
                      {item?.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ margin: "0 10px" }}>
              <InputLabel id="demo-simple-select-label">Select Building Floor 5*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingFloor"
                value={inputs?.buildingFloor}
                label="Select Building Floor 5*"
                onChange={onChange}
              >
                {buildingFloorList
                  ?.filter((floor) => floor?.buildingName?._id === inputs?.buildingName)
                  ?.map((item, index) => (
                    <MenuItem key={index} value={item?._id}>
                      {item?.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ margin: "0 10px" }}>
              <InputLabel id="demo-simple-select-label">Select Building Space 6</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingSpaceType"
                value={inputs?.buildingSpaceType}
                label="Select Building Space 6"
                onChange={onChange}
              >
                {buildingSpaceList
                  ?.filter((space) => space?.buildingFloor?._id === inputs?.buildingFloor)
                  ?.map((item, index) => (
                    <MenuItem key={index} value={item?._id}>
                      {item?.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ margin: "0 10px" }}>
              <InputLabel id="demo-simple-select-label">Space ID 7</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="areaId"
                value={inputs?.areaId}
                label="Space ID 7"
                onChange={onChange}
              >
                {buildingSpaceIdList
                  ?.filter((spaceId) => spaceId?.buildingSpaceType?._id === inputs?.buildingSpaceType)
                  ?.map((item, index) => (
                    <MenuItem key={index} value={item?._id}>
                      {item?.areaId}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ margin: "0 10px" }}>
              <InputLabel id="demo-simple-select-label">Priority Level 8</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="priorityLevel"
                value={inputs?.priorityLevel}
                label="Priority Level 8"
                onChange={onChange}
              >
                <MenuItem value="Emergency">Emergency</MenuItem>
                <MenuItem value="Urgent">Urgent</MenuItem>
                <MenuItem value="Priority 1 (24 Hours)">Priority 1 (24 Hours)</MenuItem>
                <MenuItem value="Priority 2 (48 Hours)">Priority 2 (48 Hours)</MenuItem>
                <MenuItem value="Priority 3 (72 Hours)">Priority 3 (72 Hours)</MenuItem>
                <MenuItem value="Priority 4 (72+ Hours)">Priority 4 (72+ Hours)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Issue Description 9*"
              value={inputs?.issueDescription}
              name="issueDescription"
              variant="outlined"
              fullWidth
              style={{ margin: "0 10px" }}
              onChange={onChange}
            />
            <Button variant="outlined" component="label" fullWidth sx={{ marginBottom: "15px", marginLeft: "10px" }}>
              {inputs.file ? inputs.file.name : "Upload Image"}
              <input
                type="file"
                name="file"
                hidden
                onChange={handleFileAddChange}
                accept="png, jpg, jpeg"
              />
            </Button>
          </Grid>
          <Grid
            style={{
              marginTop: "15px",
              width: "100%",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: "10px",
              marginLeft: "10px",
            }}
          >
            <Button variant="contained" onClick={onSubmit} style={{ height: "40px" }}>
              Submit
            </Button>
            <Button
              onClick={() => setModalOpen(false)}
              variant="outlined"
              style={{ height: "40px" }}
            >
              Cancel
            </Button>
          </Grid>
        </Box>
      </Modal>
      {/* Edit-FSR Modal */}
      <Modal open={editModal} onClose={() => setEditModal(false)}>
        <Box sx={style}>
          <PageTitle title={"Edit FSR"} />
          <Grid
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "10px",
            }}
          >
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Issue Type 1*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="issueType"
                value={buildingInputs?.issueType}
                onOpen={fetchIssueTypes}
                label="Priority Level 1*"
                onChange={editChange}
              >
                {issueTypes.length > 0 ? (
                  issueTypes.map((issueType) => (
                    <MenuItem key={issueType._id} value={issueType._id}>
                      {issueType.title}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No issue types available</MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Fault Type 2*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="faultType"
                value={buildingInputs?.faultType}
                label="Fault Type 2*"
                onChange={editChange}
              >
                {faultTypes.length > 0 ? (
                  faultTypes.map((faultType) => (
                    <MenuItem key={faultType._id} value={faultType._id}>
                      {faultType.title}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No fault available</MenuItem>
                )}
              </Select>
            </FormControl>
            {fSRFile && (
              <Button
                variant="outlined"
                size="small"
                style={{ marginLeft: "10px", width: "200%" }}
                onClick={() => window.open(fSRFile, "_blank")}
              >
                Refer to this Resource before submitting a service request
              </Button>
            )}
            {fSRFile && <br />}


            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Select Building Type 3*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingTypes"
                value={buildingInputs?.buildingTypes}
                label="Select Building Type 3*"
                onChange={editChange}
              >
                {buildingTypeList?.map((item, index) => (
                  <MenuItem key={index} value={item?._id}>
                    {item?.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Select Building Name 4*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingName"
                value={buildingInputs?.buildingName}
                label="Select Building Name 4*"
                onChange={editChange}
              >
                {buildingNameList
                  ?.filter((name) => name?.buildingTypes?._id === buildingInputs?.buildingTypes)
                  ?.map((item, index) => (
                    <MenuItem key={index} value={item?._id}>
                      {item?.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Select Building Floor 5*</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingFloor"
                value={buildingInputs?.buildingFloor}
                label="Select Building Floor 5*"
                onChange={editChange}
              >
                {buildingFloorList
                  ?.filter((floor) => floor?.buildingName?._id === buildingInputs?.buildingName)
                  ?.map((item, index) => (
                    <MenuItem key={index} value={item?._id}>
                      {item?.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Select Building Space 6</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingSpaceType"
                value={buildingInputs?.buildingSpaceType}
                label="Select Building Space 6"
                onChange={editChange}
              >
                {buildingSpaceList
                  ?.filter((space) => space?.buildingFloor?._id === buildingInputs?.buildingFloor)
                  ?.map((item, index) => (
                    <MenuItem key={index} value={item?._id}>
                      {item?.title}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Space ID 7</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingSpaceId"
                value={buildingInputs?.buildingSpaceId}
                label="Space ID 7"
                onChange={editChange}
              >
                {buildingSpaceIdList
                  ?.filter((spaceId) => spaceId?.buildingSpaceType?._id === buildingInputs?.buildingSpaceType)
                  ?.map((item, index) => (
                    <MenuItem key={index} value={item?._id}>
                      {item?.areaId}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Priority Level 8</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="priorityLevel"
                value={buildingInputs?.priorityLevel}
                label="Priority Level 8"
                onChange={editChange}
              >
                <MenuItem value="Emergency">Emergency</MenuItem>
                <MenuItem value="Urgent">Urgent</MenuItem>
                <MenuItem value="Priority 1 (24 Hours)">Priority 1 (24 Hours)</MenuItem>
                <MenuItem value="Priority 2 (48 Hours)">Priority 2 (48 Hours)</MenuItem>
                <MenuItem value="Priority 3 (72 Hours)">Priority 3 (72 Hours)</MenuItem>
                <MenuItem value="Priority 4 (72+ Hours)">Priority 4 (72+ Hours)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Issue Description 9*"
              value={buildingInputs?.issueDescription}
              name="issueDescription"
              variant="outlined"
              style={{ width: "100%", marginLeft: "10px" }}
              onChange={editChange}
            />
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Assignee 10</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="assignee"
                value={buildingInputs?.assignee}
                label="Assignee 10"
                onChange={editChange}
              >
                {assignee
                  ?.filter((name) => name?.buildingName?._id === buildingInputs?.buildingName)
                  ?.map((assignee, index) => (
                    <MenuItem key={index} value={assignee._id}>
                      {assignee.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Vendor 11</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingVendor"
                value={buildingInputs?.buildingVendor}
                label="Vendor 11"
                onChange={editChange}
              >
                {vendor
                  ?.filter((name) => name?.buildingName?._id === buildingInputs?.buildingName)
                  ?.map((vendor, index) => (
                    <MenuItem key={index} value={vendor._id}>
                      {vendor.vendorName}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <TextField
              label="WO Number 12"
              value={buildingInputs?.woNumber}
              name="woNumber"
              variant="outlined"
              style={{ width: "100%", marginLeft: "10px" }}
              onChange={editChange}
            />
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Status 13</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="status"
                value={buildingInputs?.status}
                label="Status 13"
                onChange={editChange}
              >
                <MenuItem value={"Submitted/Open"}>Submitted/Open</MenuItem>
                <MenuItem value={"Inactive"}>Inactive</MenuItem>
                <MenuItem value={"Assigned"}>Assigned</MenuItem>
                <MenuItem value={"Deferred"}>Deferred</MenuItem>
                <MenuItem value={"On Hold"}>On Hold</MenuItem>
                <MenuItem value={"Completed"}>Completed</MenuItem>
                <MenuItem value={"Duplicate"}>Duplicate</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Action Taken 14"
              value={buildingInputs?.actionTaken}
              name="actionTaken"
              variant="outlined"
              style={{ width: "100%", marginLeft: "10px" }}
              onChange={editChange}
            />
            <TextField
              label="Cost 15"
              value={buildingInputs?.cost}
              name="cost"
              type="number"
              variant="outlined"
              style={{ width: "100%", marginLeft: "10px" }}
              onChange={editChange}
            />
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <InputLabel id="demo-simple-select-label">Asset Id 16</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                name="buildingAssetId"
                value={buildingInputs?.buildingAssetId}
                label="Asset Id 16"
                onChange={editChange}
              >
                {buildingAssetIdList
                  ?.filter((name) => name?.buildingFloor?._id === buildingInputs?.buildingFloor)
                  ?.map((asset, index) => (
                    <MenuItem key={index} value={asset._id}>
                      {asset.assetId}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <FormControl fullWidth style={{ marginLeft: "10px" }}>
              <button type="button" onClick={handleClick}>
                {fileName}
              </button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
                accept=".pdf, image/*"
              />
            </FormControl>
          </Grid>
          <Grid
            style={{
              marginTop: "15px",
              width: "100%",
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: "10px",
              marginLeft: "10px",
            }}
          >
            <Button variant="contained" onClick={onSubmit} style={{ float: "right" }} sx={{ height: 40 }}>
              Update
            </Button>
            <Button
              onClick={() => setEditModal(false)}
              variant="outlined"
              style={{ float: "right" }}
              sx={{ height: 40 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: "#75859D" }}
              sx={{ height: 40, mx: 2 }}
              onClick={() => getExportPdf()}
            >
              Export PDF
            </Button>
            {buildingInputs.jobAttachment && (
              <Button variant="contained" style={{ backgroundColor: "#c8f0b1" }} sx={{ height: 40, mx: 2 }}>
                <a href={buildingInputs.jobAttachment} target="_blank" rel="noopener noreferrer">
                  View Attachment
                </a>
              </Button>
            )}
            <Button
              variant="contained"
              style={{ backgroundColor: "#75859D" }}
              sx={{ height: 40, mx: 2 }}
              // onClick={() => navigate("/taskManagement")}
              onClick={() => navigate("/visitor_task")}
            >
              Add VMS Task
            </Button>
          </Grid>
        </Box>
      </Modal>
    </div>
  );
};

export default FSR;
